import { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { UIProvider } from '@/app/providers/UIContext';
import { AuthProvider } from '@/features/auth/AuthContext';
import { CartProvider } from '@/features/commerce/CartContext';
import { CartModal, type Quote } from '@/features/commerce/CartModal';
import { CheckoutModal } from '@/features/commerce/CheckoutModal';
import { ShipModal } from '@/features/commerce/ShipModal';
import { CounselModal, type CounselPreset } from '@/features/counselling/CounselModal';
import { DoubtModal } from '@/features/support/DoubtModal';
import { ContactFloats } from '@/components/layout/public/ContactFloats';
import { Footer } from '@/components/layout/public/Footer';
import { Header } from '@/components/layout/public/Header';
import { SiteWideBanner } from '@/components/layout/public/SiteWideBanner';
import { AffiliateRefCapture } from '@/features/affiliate/AffiliateRefCapture';
import { Toast } from '@/components/common/Toast';
import { ScrollToTop } from '@/components/common/ScrollToTop';

/**
 * App shell. Header and footer are light on every route; the dark theme is
 * scoped to Home only (spec 7.1) when that page lands in Phase 9.
 *
 * The cart/checkout/ship modals live here rather than in a page because the
 * cart button is in the header and must open from anywhere — mirroring the
 * prototype, where they were top-level modals in the document.
 */
export function Layout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isStaffPanel =
    pathname === '/admin' ||
    pathname.startsWith('/admin/') ||
    pathname === '/instructor' ||
    pathname.startsWith('/instructor/') ||
    pathname === '/partner' ||
    pathname.startsWith('/partner/') ||
    pathname === '/order-manager' ||
    pathname.startsWith('/order-manager/') ||
    pathname === '/dashboard' ||
    pathname.startsWith('/dashboard/');
  const isAuthPage =
    pathname === '/login' || pathname === '/register' || pathname === '/affiliate/register';
  const hidePublicChrome = isStaffPanel || isAuthPage;
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState<{ quote: Quote; coupon: string | null } | null>(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [counsel, setCounsel] = useState<CounselPreset | null>(null);
  const [doubtSlug, setDoubtSlug] = useState<string | null>(null);

  const toast = useCallback((message: string) => {
    // Re-arming with the same string must still re-show the bar; nudging the
    // state to null first makes the effect re-run rather than no-op.
    setToastMsg(null);
    setTimeout(() => setToastMsg(message), 0);
  }, []);

  const openAuth = useCallback(
    (m: 'login' | 'signup') => {
      // Only carry deep links worth returning to — not the marketing home.
      const from =
        pathname !== '/' &&
        !pathname.startsWith('/login') &&
        !pathname.startsWith('/register')
          ? pathname
          : undefined;
      navigate(m === 'login' ? '/login' : '/register', from ? { state: { from } } : undefined);
    },
    [navigate, pathname],
  );

  const ui = useMemo(
    () => ({
      openCart: () => setCartOpen(true),
      openAuth,
      toast,
      openCounsel: (preset?: CounselPreset) => setCounsel(preset ?? {}),
      openDoubt: (slug: string) => setDoubtSlug(slug),
    }),
    [toast, openAuth],
  );

  return (
    <AuthProvider>
      <CartProvider>
        <UIProvider value={ui}>
        <ScrollToTop />
        <AffiliateRefCapture />
        {!hidePublicChrome ? (
          <>
            <SiteWideBanner />
            <Header onOpenCart={() => setCartOpen(true)} />
          </>
        ) : null}
        <Outlet />
        {!hidePublicChrome ? <Footer /> : null}
        {!hidePublicChrome ? <ContactFloats /> : null}

        {cartOpen ? (
          <CartModal
            onClose={() => setCartOpen(false)}
            onCheckoutCourses={(quote, coupon) => {
              setCartOpen(false);
              setCheckout({ quote, coupon });
            }}
            onCheckoutBooks={() => {
              setCartOpen(false);
              setShipOpen(true);
            }}
          />
        ) : null}

        {checkout ? (
          <CheckoutModal quote={checkout.quote} coupon={checkout.coupon} onClose={() => setCheckout(null)} />
        ) : null}

        {shipOpen ? <ShipModal onClose={() => setShipOpen(false)} /> : null}

        {counsel ? (
          <CounselModal preset={counsel} onClose={() => setCounsel(null)} onBooked={toast} />
        ) : null}

        {doubtSlug ? <DoubtModal courseSlug={doubtSlug} onClose={() => setDoubtSlug(null)} /> : null}

        {/* .toast has been in base.css since Phase 3 with nothing to render it. */}
        <Toast message={toastMsg} onDone={() => setToastMsg(null)} />
        </UIProvider>
      </CartProvider>
    </AuthProvider>
  );
}
